import { useCallback, useEffect, useRef } from 'react';
import { useMailRuntime } from '../context/MailRuntime';
import { useComposerContext } from '../context/ComposerContext';
import { composerRequest } from '../composerRequest';

export default function useTemplates(draft) {
  const ctx = useComposerContext(), runtime = useMailRuntime();
  const pending = useRef(null), alive = useRef(true);
  useEffect(()=>{alive.current=true;return()=>{alive.current=false;};},[]);
  const { API_GET_TEMPLATES, API_SAVE_TEMPLATE, API_GET_TEMPLATE } = runtime.api;
  const loadTemplatesFromApi = useCallback(async () => {
    try {
      const items = [], seen = new Set(); let token;
      do {
        const data = await composerRequest(runtime, ctx.owner, API_GET_TEMPLATES + (token ? '?nextToken=' + encodeURIComponent(token) : ''));
        if (!alive.current) return;
        if (!Array.isArray(data.templates) || data.templates.some(t => typeof t.id !== 'string' || typeof t.name !== 'string')) throw new Error('Unreadable template list.');
        items.push(...data.templates); token = data.nextToken;
        if (token && (typeof token !== 'string' || seen.has(token))) throw new Error('Invalid template pagination.');
        if (token) seen.add(token);
      } while (token);
      ctx.setTemplates(items);
    } catch (error) { ctx.setNotice(error.message || 'Unable to load templates. Use Reload templates to retry.'); }
  }, [runtime, ctx, API_GET_TEMPLATES]);
  const saveCurrentAsTemplate = useCallback(async () => {
    if (!ctx.ready || ctx.operationLock.current) return;
    const name = runtime.fixedTemplate?.name || ctx.newTemplateName.trim(); if (!name) { ctx.setNotice('Name your template first.'); return; }
    ctx.operationLock.current = true; ctx.setSaving(true);
    try {
      if (runtime.fixedTemplate && !ctx.templates.some(t => t.id === runtime.fixedTemplate.id)) throw new Error('Reload templates before saving.');
      if (!pending.current || pending.current.name !== name) pending.current = { name, id: crypto.randomUUID() };
      const selected = runtime.fixedTemplate && ctx.templates.find(t => t.id === runtime.fixedTemplate.id);
      const payload = { ...draft.capture(), templateId: selected?.id || pending.current.id, name, version: selected ? ctx.templateVersion : null };
      payload.bodyHtml = payload.body;
      const result = await composerRequest(runtime, ctx.owner, API_SAVE_TEMPLATE, 'POST', payload);
      if (!alive.current) return;
      if ((!result.success && !result.saved) || (runtime.storageScope && (result.templateId !== payload.templateId || !result.version))) throw new Error('Template save was not confirmed. Reload templates before retrying.');
      if (runtime.fixedTemplate) ctx.setTemplateVersion(result.version);
      pending.current = null; ctx.setNewTemplateName(''); ctx.setNotice('Template saved to your account.'); await loadTemplatesFromApi();
    } catch (error) { ctx.setNotice(error.message); }
    finally { ctx.operationLock.current = false; ctx.setSaving(false); }
  }, [ctx, runtime, draft, API_SAVE_TEMPLATE, loadTemplatesFromApi]);
  const applyTemplate = useCallback(async () => {
    if (!ctx.ready || ctx.operationLock.current || !ctx.selectedTemplateId) return;
    if (!window.confirm('Replace the current design with this template? Save your current draft first if you want to keep it.')) return;
    ctx.operationLock.current = true; ctx.setLoading(true);
    try {
      const data = await composerRequest(runtime, ctx.owner, API_GET_TEMPLATE + '?id=' + encodeURIComponent(ctx.selectedTemplateId));
      if (!alive.current) return;
      const template = data.template || data;
      if (runtime.storageScope && (template.templateId !== ctx.selectedTemplateId || !template.version)) throw new Error('Template could not be confirmed. Reload templates.');
      draft.restoreDocument(template, true); ctx.setNotice('Template applied. Save to update your draft.');
    } catch (error) { ctx.setNotice(error.message); }
    finally { ctx.operationLock.current = false; ctx.setLoading(false); }
  }, [ctx, runtime, draft, API_GET_TEMPLATE]);
  const deleteTemplate = useCallback(async () => {
    if (!ctx.ready || ctx.operationLock.current || !ctx.selectedTemplateId) return;
    const selected = ctx.templates.find(t => t.id === ctx.selectedTemplateId);
    if (!selected?.version) { ctx.setNotice('Reload templates before deleting.'); return; }
    if (!window.confirm(`Delete the saved template “${selected.name}”? Existing drafts are kept.`)) return;
    ctx.operationLock.current = true; ctx.setSaving(true);
    try {
      const data = await composerRequest(runtime, ctx.owner, API_SAVE_TEMPLATE, 'POST', { action: 'delete', templateId: selected.id, version: selected.version });
      if (!alive.current) return;
      if (!data.saved || (runtime.storageScope && (data.templateId !== selected.id || !data.version))) throw new Error('Template deletion was not confirmed.');
      ctx.setSelectedTemplateId(''); ctx.setNotice('Template deleted from your account.'); await loadTemplatesFromApi();
    } catch (error) { ctx.setNotice(error.message); }
    finally { ctx.operationLock.current = false; ctx.setSaving(false); }
  }, [ctx, runtime, API_SAVE_TEMPLATE, loadTemplatesFromApi]);
  const loadInitialTemplate = useCallback(async ({ preserveOnFailure = false } = {}) => {
    if (ctx.operationLock.current) return;
    const keepCurrent = preserveOnFailure && ctx.ready;
    ctx.operationLock.current = true;
    ctx.setReady(false); ctx.setLoading(true); ctx.setLoadError('');
    try {
      const data = await composerRequest(runtime, ctx.owner, API_GET_TEMPLATE + '?id=' + encodeURIComponent(runtime.fixedTemplate.id));
      if (!alive.current) return;
      if (data.template?.templateId !== runtime.fixedTemplate.id || !data.template.version) throw new Error('The template could not be loaded.');
      draft.restoreDocument({ ...data.template, newsletterId: null, version: null, templateVersion: data.template.version });
      ctx.setReady(true);
    } catch (error) { if (alive.current) {
      if (keepCurrent) { ctx.setReady(true); ctx.setNotice('The saved template could not be opened. Your current draft is unchanged and can still be saved. ' + error.message); }
      else ctx.setLoadError(error.message);
    } }
    finally { ctx.operationLock.current = false; if (alive.current) ctx.setLoading(false); }
  }, [ctx, runtime, draft, API_GET_TEMPLATE]);
  return { loadInitialTemplate, loadTemplatesFromApi, saveCurrentAsTemplate, applyTemplate, deleteTemplate };
}
